import { cn } from "@bcc/lib/utils";
import { CheckCircle } from "lucide-react";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import {
  setReviewSuccessBadgeTestId,
  setReviewSuccessDescriptionTestId,
  setReviewSuccessExitTestId,
  setReviewSuccessIconTestId,
  setReviewSuccessImportMoreTestId,
  setReviewSuccessRedirectingTestId,
  setReviewSuccessTestId,
  setReviewSuccessTitleTestId,
} from "./review-success.utils";

interface ReviewSuccessProps {
  title: string;
  description: string;
  redirectingText: string;
  /** Imported coffees rendered as green badges (batch import).
   *  `id` is the stable per-product key (uuid ?? id); `name` is the display title. */
  products?: Array<{ id: string; name: string }>;
  /** When provided, renders an "Import more products" button. */
  onImportMore?: () => void;
  importMoreLabel?: string;
  /** When provided, renders an "Exit" button. */
  onExit?: () => void;
  exitLabel?: string;
  testId?: string;
  className?: string;
}

export function ReviewSuccess({
  title,
  description,
  redirectingText,
  products,
  onImportMore,
  importMoreLabel,
  onExit,
  exitLabel,
  testId,
  className,
}: Readonly<ReviewSuccessProps>) {
  return (
    <div
      data-testid={testId ?? setReviewSuccessTestId("default")}
      className={cn("flex flex-col items-center justify-center py-12 text-center", className)}
    >
      <div
        data-testid={setReviewSuccessIconTestId("default")}
        className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20 mb-4"
      >
        <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
      </div>
      <h3 data-testid={setReviewSuccessTitleTestId("default")} className="text-xl font-semibold mb-2">
        {title}
      </h3>
      <p data-testid={setReviewSuccessDescriptionTestId("default")} className="text-muted-foreground mb-4">
        {description}
      </p>
      {products && products.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
          {products.map((product) => (
            <Badge
              key={product.id}
              data-testid={setReviewSuccessBadgeTestId(product.id)}
              className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
            >
              {product.name}
            </Badge>
          ))}
        </div>
      )}
      <p data-testid={setReviewSuccessRedirectingTestId("default")} className="text-sm text-muted-foreground">
        {redirectingText}
      </p>
      {((onImportMore && importMoreLabel) || (onExit && exitLabel)) && (
        <div className="flex items-center justify-center gap-3 mt-6">
          {onImportMore && importMoreLabel && (
            <Button variant="outline" onClick={onImportMore} data-testid={setReviewSuccessImportMoreTestId("default")}>
              {importMoreLabel}
            </Button>
          )}
          {onExit && exitLabel && (
            <Button onClick={onExit} data-testid={setReviewSuccessExitTestId("default")}>
              {exitLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
